<template>
  <div
    :class="{ onlyTextFilter: onlyTextFilter }"
    class="filters">
    <input
      v-if="filters.text"
      v-model="textFilter"
      :aria-label="filters.text.label"
      :placeholder="filters.text.label"
      :style="{ width: filterTextWidth }"
      type="text"
      class="form-control"
      @keyup.enter="$emit('onEnter')">
    <template v-if="!onlyTextFilter">
      <em
        id="filters-trigger"
        :title="string.filterIconTitle"
        :class="{ active: isActiveFilters }"
        class="qx-icon icon-filter filter-icon"
        aria-hidden="true"
        @click="togglePopover" />
      <qx-popover
        target="#filters-trigger"
        custom-class="filter-popover"
        location="bottom end"
        origin="end top"
        @hidden="hidePopover">
        <div
          class="filters-container"
          tabindex="1">
          <div class="mb-9">
            <template v-if="filters.main?.length > 0">
              <div
                v-for="(filter, index) in filters.main"
                :key="filter.id">
                <div
                  v-if="index !== 0"
                  class="delimiter" />
                <div
                  v-if="filter.title"
                  class="list-title">
                  {{ filter.title }}
                </div>
                <div
                  v-for="item in filter.items"
                  :key="`filter-${item.id}`"
                  class="item-box">
                  <qx-checkbox
                    :id="`filter-main-${item.id}`"
                    :model-value="filterStates[item.id]"
                    :checked="filterStates[item.id]"
                    :label="item.label"
                    class="filter"
                    @on-change="onFilterChange(item.id, !filterStates[item.id])" />
                  <div class="item-count">
                    {{ getFilteredList({ field: item.field, value: item.value }).length }}
                  </div>
                </div>
              </div>
            </template>
            <template v-if="filters.intersection && filters.intersection.length">
              <div
                v-for="filter in filters.intersection"
                :key="filter.id">
                <div class="delimiter" />
                <div
                  v-if="filter.title"
                  class="list-title">
                  {{ filter.title }}
                </div>
                <div
                  v-for="item in getVisibleFilters(filter.items)"
                  :key="`filter-${item.id}`"
                  class="item-box">
                  <qx-checkbox
                    :id="`filter-intersection-${item.id}`"
                    :model-value="filterStates[item.id]"
                    :checked="filterStates[item.id]"
                    :label="item.label"
                    class="filter"
                    @on-change="onFilterChange(item.id, !filterStates[item.id])" />
                  <div class="item-count">
                    {{ getFilteredList({ field: item.field, value: item.value }).length }}
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="filer-actions d-flex justify-space-between">
            <button
              v-translate
              class="clear-button"
              @click="onClearAll">
              Clear all
            </button>
          </div>
        </div>
      </qx-popover>
    </template>
  </div>
</template>

<script>
import qxCheckbox from '@/elements/form/QxCheckbox.vue';
import QxPopover from '@/elements/QxPopover.vue';
import { hasActiveFilter } from '@/elements/utils.js';
import { getListByFilter } from '@/utils/filters.js';

export default {
  name: 'QxFilter',
  components: {
    qxCheckbox,
    QxPopover,
  },
  props: {
    collection: {
      type: Array,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterStates: {
      type: Object,
      required: true,
    },
    filterTextWidth: {
      type: String,
      default: '190px',
    },
  },
  emits: ['onChangeFilter', 'onClearFilters', 'onChangeTextFilter', 'onEnter'],
  data() {
    return {
      isPopoverOpen: false,
    };
  },
  computed: {
    string() {
      return {
        filterIconTitle: this.$gettext('Filter'),
      };
    },

    onlyTextFilter() {
      return !this.filters.main && !this.filters.intersection;
    },
    isActiveFilters() {
      return (
        hasActiveFilter(this.filters.main, this.filterStates) ||
        hasActiveFilter(this.filters.intersection, this.filterStates)
      );
    },
    textFilter: {
      get() {
        return this.filterStates[this.filters.text.id];
      },
      set(newValue) {
        this.onTextFilterChange(newValue);
      },
    },
  },
  methods: {
    getVisibleFilters(items) {
      return items.filter((filter) => (typeof filter.isVisible !== 'undefined' ? filter.isVisible : true));
    },
    onFilterChange(prop, value) {
      this.$emit('onChangeFilter', { prop, value });
    },
    onClearAll() {
      this.$emit('onClearFilters');
    },
    onTextFilterChange(textValue) {
      this.$emit('onChangeTextFilter', textValue);
    },
    getFilteredList({ field, value, collection = this.collection }) {
      return getListByFilter({ field, value, collection });
    },
    togglePopover() {
      this.isPopoverOpen = !this.isPopoverOpen;
    },
    hidePopover() {
      this.isPopoverOpen = false;
    },
  },
};
</script>

<style lang="scss">
.filter-popover {
  padding: 0 8px;
  border: 1px solid $gray-300;
  overflow-y: auto;
  max-height: 50vh;
  overflow-x: hidden;
  .filters-container {
    max-width: 380px;
    .list-title {
      color: $grey;
      text-transform: uppercase;
      font-size: 11.5px;
      padding-bottom: 10px;
      padding-top: 3px;
    }

    .item-box {
      @extend %dspFlex;
      @include flex-direction(flex-row);
      line-height: 27px;
      align-items: center;
    }

    .item-count {
      border-radius: 4px;
      border: 1px solid grey;
      color: grey;
      line-height: 12px;
      font-size: 10px;
      padding: 0 4px;
      margin-left: 8px;
      height: 14px;
    }

    .delimiter {
      border-top: $section-border;
      margin: 4px -9px;
    }

    .filer-actions {
      position: absolute;
      bottom: 0;
      width: calc(100% - 16px);
      padding-top: 12px;
      padding-bottom: 4px;
      background-color: white;

      .clear-button {
        border: 0;
        background-color: white;
        padding: 0;
        font-size: 13px;
        margin: 0 auto;
        color: $grey;
      }
    }
    .custom-checkbox {
      .custom-label {
        max-width: 260px;
        text-overflow: ellipsis;
        overflow-x: hidden;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.filters {
  @extend %dspFlex;
  position: relative;
  padding: 6px 13px 3px 0;
  @include justify-content(flex-end);
  @include align-items(center);

  .filter-icon {
    line-height: 26px;
    margin-left: 12px;
    color: $icon-grey;
    &.active,
    &:hover {
      color: $base-blue;
      cursor: pointer;
    }
  }

  &.onlyTextFilter {
    padding-right: 0;
    padding-left: 0;

    input {
      width: 100%;
    }
  }
}
</style>
